import type { MessageKey } from "../index";

/**
 * German message catalogue. `satisfies Record<MessageKey, string>` turns a
 * missing key into a compile error; `satisfies` preserves the literal types
 * the accessor uses to cross-check placeholders.
 */
export const de = {
  title: "egalregal",
  homeHint: "Tippen, um dein Lager zu durchsuchen",
  searchPlaceholder: "Boxen, Regale, Räume suchen…",
  search: "Suche",
  searchMode: "Suchmaschine",
  searchModeKeyword: "Stichwort",
  searchModeSemantic: "Semantisch",
  searchRecent: "Zuletzt geöffnet",
  clearSearch: "Suche löschen",
  scopeAll: "Alle",
  scopeContainers: "Behälter",
  scopeItems: "Artikel",
  scopePhotos: "Fotos",
  browse: "Blättern",
  add: "Hinzufügen",
  home: "Start",
  primaryNav: "Hauptnavigation",
  breadcrumb: "Navigationspfad",
  switchLanguage: "Sprache wechseln zu {language}",
  online: "Online",
  offline: "Offline",
  login: "Anmelden",
  username: "Benutzername",
  password: "Passwort",
  loginFailed: "Anmeldung fehlgeschlagen – Benutzername oder Passwort prüfen.",
  oauth2Failed: "SSO-Anmeldung fehlgeschlagen – versuche es erneut oder nutze dein Passwort.",
  loginWithProvider: "Mit {provider} anmelden",
  localAuthDisabled: "Passwort-Anmeldung ist deaktiviert – nutze deinen Identitätsanbieter.",
  demoBanner: "Schreibgeschützte Demo — starte egalregal lokal, um Änderungen vorzunehmen.",
  or: "oder",
  loading: "Wird geladen…",
  logout: "Abmelden",
  homeTitle: "Dein Lager",
  noContainers: "Noch keine Behälter — füge deinen ersten hinzu.",
  browseHint: "Blättern nach Ort folgt als Nächstes.",
  addTitle: "Behälter hinzufügen",
  editTitle: "Behälter bearbeiten",
  formName: "Name",
  formDescription: "Beschreibung",
  formOwner: "Eigentümer",
  ownerSuggestions: "Zuletzt genutzte Eigentümer",
  formSpace: "Ort",
  formKind: "Art",
  formTags: "Tags",
  tagsHint: "Zuletzt genutzte Tags zuerst; tippen für eigene Tags",
  save: "Speichern",
  cancel: "Abbrechen",
  close: "Schließen",
  edit: "Bearbeiten",
  delete: "Löschen",
  back: "Zurück",
  backToSearch: "Zurück zu den Suchergebnissen",
  backToScan: "Zurück zum Scanner",
  backToStorage: "Zurück zum Lager",
  allStorage: "Alle Lagerorte",
  unsaved: "Ungespeicherte Änderungen",
  discardTitle: "Änderungen verwerfen?",
  discardBody: "Dieser Behälter hat ungespeicherte Änderungen.",
  keepEditing: "Weiter bearbeiten",
  discard: "Verwerfen",
  collapseNav: "Navigation einklappen",
  expandNav: "Navigation ausklappen",
  offlineBanner: "Offline – zwischengespeicherte Daten",
  next: "Weiter",
  menu: "Menü",
  saved: "Gespeichert",
  created: "Erstellt",
  createAsChild: "Neuer Unterbehälter von {parent}",
  createLinkError:
    "Auf der obersten Ebene erstellt, aber konnte nicht unter {parent} hinzugefügt werden: {error}",
  // Schnellerfassung: der flüchtige Elternort neuer Behälter (Ticket 12).
  quickAddTitle: "Schnellerfassung",
  quickAddHint: "Schnellerfassung: Neue Behälter können unter einem Ort angelegt werden.",
  quickAddSet: "Aktuellen Ort festlegen",
  quickAddUnder: "Neue Behälter werden unter {path} angelegt",
  quickAddOverride:
    "Dieser Behälter landet unter {parent} (einmalig). Die nächsten Behälter werden unter {path} angelegt.",
  quickAddChange: "Ort ändern",
  quickAddStop: "Automatisches Verknüpfen beenden",
  quickAddPickTitle: "Aktuellen Ort wählen",
  saveError: "Konnte nicht speichern: {error}",
  saveEntryError: "Gespeichert, aber der Katalog konnte nicht geändert werden: {error}",
  captureCamera: "Kamera",
  captureRecord: "Aufnehmen",
  captureFile: "Datei",
  captureTakePhoto: "Foto aufnehmen",
  captureRetake: "Neu aufnehmen",
  captureKeep: "Übernehmen",
  captureStop: "Stopp",
  capturePause: "Pause",
  captureResume: "Fortsetzen",
  capturePreview: "Aufgenommenes Foto",
  captureCameraError: "Kamera nicht verfügbar – nutze stattdessen Datei.",
  captureMicError: "Mikrofon nicht verfügbar – nutze stattdessen Datei.",
  cameraSaveError: "Fotos konnten nicht gespeichert werden – ist der Server erreichbar?",
  files: "Fotos & Sprachnotizen",
  record: "Sprachnotiz",
  noTranscript: "Noch kein Transkript.",
  includeSubContainerMedia: "Medien aus Unterbehältern einbeziehen",
  containerKinds: "Behälterarten",
  noSubContainerMedia: "Keine passenden Medien in Unterbehältern.",
  transcriptHeading: "Transkript",
  removeFile: "Entfernen",
  removeFileFailed: "Datei konnte nicht entfernt werden.",
  browseTitle: "Nach Ort blättern",
  containersTitle: "Behälter",
  move: "Verschieben",
  link: "Verknüpfen",
  unlink: "Lösen",
  linkChildTitle: "Diesen Behälter verknüpfen unter…",
  details: "Details",
  // Merged container view (container-view 01): one menu line per section.
  more: "Mehr",
  view: "Ansicht",
  tree: "Baum",
  menuContainer: "Behälter",
  hideDetails: "Details ausblenden",
  showDetails: "Details anzeigen",
  addSubContainer: "Unterbehälter hinzufügen",
  selectContainers: "Behälter auswählen",
  linkFailed: "Behälter konnte nicht verknüpft werden.",
  unlinkFailed: "Verknüpfung konnte nicht gelöst werden.",
  moveTitle: "Behälter verschieben nach…",
  moveToRoot: "Oberste Ebene",
  movePickEntry: "Ziel-Katalogeintrag wählen",
  moveNoEntries: "Keine Katalogeinträge verfügbar.",
  movePickParent: "Platz in {entry} wählen",
  moveNoContainers: "Keine Behälter in diesem Eintrag.",
  moveTransferTitle: "Eigentum übertragen",
  moveTransferBody: "Du bist dabei, diesen Behälter und seinen gesamten Unterbaum nach {entry} zu verschieben.",
  moveTransferWarning: "Nur ein Zurückverschieben ist möglich; Artikelplatzierungen werden entfernt.",
  moveTransferConfirm: "Eigentum übertragen",
  // Container bulk mode and its destination dialog (bulk-move 05)
  bulkMoveTitle: "Unterbehälter verschieben nach…",
  bulkSelected: "{count} ausgewählt",
  bulkMoveAction: "Verschieben…",
  bulkSelectAll: "Alle auswählen",
  bulkCapHint: "Es können nur die ersten {count} Zeilen ausgewählt werden.",
  bulkPickEntry:
    "Eigene Kataloge und Kataloge mit Schreibrecht. Ein geteilter Katalog in einem anderen Zuhause ist eine Eigentumsübertragung.",
  bulkDestinationCurrent: "Aktueller Katalog",
  bulkTransferChip: "Übertragen",
  bulkTargetSearch: "Behälter in {entry} suchen",
  bulkTargetNoMatches: "Keine Behälter passen zu „{query}“.",
  bulkMoveNoOp: "Diese Behälter sind bereits an diesem Ziel.",
  bulkMoveConfirm: "{count} verschieben",
  bulkTransferBody:
    "{count} Behälter und ihre gesamten Unterbäume als Wurzeln von {entry} übertragen.",
  bulkTransferWarning:
    "Nur ein Zurückverschieben führt zurück; Artikelplatzierungen in den übertragenen Unterbäumen werden entfernt.",
  bulkTransferConfirm: "{count} übertragen",
  bulkMoveSuccess: "{count} Behälter nach {destination} verschoben.",
  bulkMoveSuccessDropped:
    "{count} Behälter nach {destination} verschoben; {dropped} Artikelplatzierungen entfernt.",
  bulkMovePartial: "{moved} von {total} verschoben; {failed} fehlgeschlagen: {reasons}",
  bulkMovePartialDropped: "{dropped} Artikelplatzierungen entfernt.",
  bulkMoveFailed: "Das Sammelverschieben ist fehlgeschlagen.",
  bulkMoveRowsChanged: "Einige ausgewählte Behälter haben sich geändert; neu laden und erneut versuchen.",
  bulkDestinationLoadFailed: "Der Zielkatalog konnte nicht geladen werden.",
  // Item bulk mode and its destination dialog (bulk-move 06)
  itemBulkMoveTitle: "Artikelplatzierungen verschieben nach…",
  itemBulkPickEntry:
    "Wähle einen Katalog im selben Zuhause. Mengen bleiben erhalten und werden mit einer vorhandenen Platzierung zusammengeführt.",
  itemBulkPickContainer: "Behälter in {entry} wählen",
  itemBulkSourceNode: "Quelle",
  itemBulkMoveConfirm: "{count} Artikel verschieben",
  itemBulkMoveSuccess: "{count} Artikel nach {destination} verschoben.",
  itemBulkMoveFailed: "Das Verschieben der Artikel ist fehlgeschlagen.",
  movedToEntry: "Verschoben nach {entry}",
  movedDroppedPlacements: "Verschoben; {count} Artikelplatzierungen entfernt.",
  hereEmpty: "Hier ist noch nichts",
  topLevel: "Oberste Ebene",
  searchResults: "Suchergebnisse",
  noMatches: "Keine Treffer.",
  trash: "Papierkorb",
  trashEmpty: "Der Papierkorb ist leer.",
  restore: "Wiederherstellen",
  purge: "Endgültig löschen",
  purgeConfirmTitle: "Endgültig löschen?",
  purgeConfirmBody: "Dies löscht den Behälter endgültig und kann nicht rückgängig gemacht werden.",
  deleteConfirmTitle: "Diesen Behälter löschen?",
  deleteConfirmBody: "Er wandert in den Papierkorb und kann wiederhergestellt werden.",
  deleteConfirmChildren: "Dieser Behälter enthält {count} Behälter. Sie werden gelöst, nicht gelöscht.",
  deleteSectionTitle: "Behälter löschen",
  deleteError: "Behälter konnte nicht gelöscht werden: {error}",
  deletedAt: "Gelöscht",
  print: "Drucken",
  printTitle: "QR-Etiketten",
  printLabels: "Etiketten drucken ({count})",
  printSize: "Rastergröße",
  printColumns: "Spalten",
  printRows: "Zeilen",
  scan: "Scannen",
  scanTitle: "Code scannen",
  scanCameraError: "Kamera nicht verfügbar — gib unten die Behälter-ID ein.",
  scanCameraErrorBarcode: "Kamera nicht verfügbar — gib unten den Barcode ein.",
  scanCameraUnavailable: "Kamera nicht verfügbar",
  scanScanning: "Scannen",
  scanAim: "Kamera ausrichten",
  scanTapToAim: "Sucher antippen zum Zielen",
  scanAimPersisted: "Anzeige wird gespeichert · schließt beim nächsten Scan",
  scanCloseAim: "Zielansicht schließen",
  scanDisplayCover: "Füllend",
  scanDisplayContain: "Ganzes Bild",
  scanDisplayScanArea: "Scanbereich",
  scanViewItem: "Artikel ansehen",
  scanNewLabel: "Neues Etikett {id}",
  scanCopyPayload: "Kopieren",
  scanOpenLink: "Link öffnen",
  scanPayloadCopied: "In die Zwischenablage kopiert.",
  scanFallbackLabel: "Behälter-ID (UUID)",
  scanNotFound: "Nicht gefunden — ID prüfen und erneut versuchen.",
  scanToLink: "Scannen & verknüpfen",
  scanLinkHint: "Der angezeigte Behälter wird als Kind des gescannten Behälters verknüpft.",
  scanLinkFailed: "Verknüpfung fehlgeschlagen — der gescannte Behälter liegt in diesem Behälter oder kann nicht als dessen Elternbehälter dienen.",
  linkedAsChild: '„{child}“ ist jetzt in „{parent}“.',
  scanSubmit: "Öffnen",
  scanEmpty: "Etikett scannen, um den Behälter hier zu sehen.",
  scanCreateNew: "Behälter anlegen",
  scanCreateItem: "Artikel anlegen",
  scanUnknownLabel: "Etikett {id} ist noch nicht vergeben — als Behälter oder als Artikel anlegen.",
  scanUnsupportedFormat: "Nicht unterstütztes Format — derzeit werden nur einfache UUID-Etiketten unterstützt.",
  scanOfflineUncached: "Du bist offline und dieser Code ist nicht im lokalen Cache.",
  scanRetry: "Erneut versuchen",
  // Payload-Ansicht und sichere Aktionen (results-06)
  scanPayloadUrl: "URL",
  scanPayloadWifi: "WLAN-Netzwerk",
  scanPayloadText: "Text",
  scanCopyPassword: "Passwort kopieren",
  scanPayloadPasswordCopied: "Passwort in die Zwischenablage kopiert.",
  scanPayloadCopyFailed: "Kopieren fehlgeschlagen – markiere den Text und kopiere ihn manuell.",
  scanPayloadTruncated:
    "Gespeicherter Inhalt ist auf {limit} Zeichen begrenzt – nur der gespeicherte Teil kann kopiert oder angezeigt werden.",
  scanPayloadShowFull: "Vollständigen Inhalt zeigen",
  scanPayloadShowLess: "Weniger anzeigen",
  scanPayloadTextOnly: "Nur Text – wird nie ausgeführt oder geladen.",
  scanPayloadOpenTitle: "Diesen Link öffnen?",
  scanPayloadOpenBody:
    "Das Ziel wird in einem neuen Browser-Tab geöffnet. Fahre nur fort, wenn du der Quelle des Codes vertraust.",
  scanPayloadOpenConfirm: "Link öffnen",
  scanPayloadSsid: "SSID",
  scanPayloadSecurity: "Sicherheit",
  scanPayloadOpenNetwork: "Offen",
  scanPayloadHidden: "Verstecktes Netzwerk",
  // Unbekannter Produkt-Barcode (results-02)
  scanProductAdd: "Zu meinen Artikeln hinzufügen",
  scanProductHint: "Anbieter-Vorschau – noch nicht in deiner Sammlung. Hinzufügen legt einen Artikel ohne Behälter an.",
  scanProductLooking: "Barcode wird gesucht …",
  scanProductMiss: "Kein Anbieter kennt den Barcode {barcode} – ergänze die Details selbst.",
  scanProductOffline: "Offline – die Barcode-Suche ist nicht verfügbar. Ergänze die Details selbst.",
  scanProductError: "Barcode-Suche fehlgeschlagen – ergänze die Details selbst.",
  scanProductAdded: "{name} zu deinen Artikeln hinzugefügt.",
  scanProductCreateFailed: "Artikel konnte nicht angelegt werden. Es wurde nichts gespeichert.",
  // Scan-Verlauf (results-05)
  scanManualOpen: "Manuelle Eingabe",
  scanHistoryOpen: "Scan-Verlauf anzeigen",
  scanHistoryTitle: "Scan-Verlauf",
  scanHistoryClose: "Scan-Verlauf schließen",
  scanHistoryCount: "{count} von {limit} Scans",
  scanHistoryHint:
    "Neueste zuerst. In diesem Browser für dein Konto gespeichert – nichts wird an den Server gesendet.",
  scanHistoryEmpty:
    "Noch keine Scans. Jeder Code landet hier – Etiketten, Produkt-Barcodes, URLs, WLAN und Text.",
  scanHistoryClear: "Verlauf löschen",
  scanHistoryClearTitle: "Scan-Verlauf löschen?",
  scanHistoryClearBody:
    "Damit werden alle gespeicherten Scans dieses Kontos in diesem Browser entfernt. Das lässt sich nicht rückgängig machen.",
  scanHistoryLoad: "Laden",
  scanHistoryUnknownLabel: "Unbekanntes Etikett",
  scanHistoryUnavailable: "Ziel existiert nicht mehr",
  scanHistoryOffline: "Offline – nicht im lokalen Cache",
  scanHistoryTrashed: "Im Papierkorb",
  scanHistoryGlyphUrl: "URL",
  scanHistoryGlyphWifi: "WLAN",
  scanHistoryGlyphText: "TXT",
  scanHistoryPayloadUrl: "URL",
  scanHistoryPayloadWifi: "WLAN · {ssid}",
  scanHistoryPayloadWifiGeneric: "WLAN-Netzwerk",
  scanHistoryPayloadText: "Text",
  scanHistoryJustNow: "gerade eben",
  scanHistoryMinutesAgo: "vor {count} Min.",
  scanHistoryHoursAgo: "vor {count} Std.",
  scanHistoryDaysAgo: "vor {count} Tagen",
  barcodeTypeQr: "QR",
  scanTrashNotice: "Dieser Behälter liegt im Papierkorb.",
  export: "Export",
  printBlank: "Leere Etiketten",
  printBlankCount: "Etiketten",
  printKindFilter: "Nach Art filtern",
  printFilterAll: "Alle",
  printFilterBoxes: "Boxen",
  printFilterNonBoxes: "Nicht-Boxen",
  printShowing: "Zeige {start}–{end} von {total}",
  printLayout: "Layout",
  printLayoutStack: "Stapel",
  printLayoutRow: "Reihe",
  printLayoutSplit: "Geteilt",
  printShowIcons: "Symbole",
  printShowQr: "QR",
  printShowShortId: "Kurz-ID",
  printMarginMm: "Rand (mm)",
  printMarginMode: "Rand-Modus",
  printMarginModeUniform: "Einheitlich",
  printMarginModeXy: "X / Y",
  printMarginModeEdges: "Pro Kante",
  printMarginX: "Rand X (mm)",
  printMarginY: "Rand Y (mm)",
  printMarginTop: "Oben (mm)",
  printMarginRight: "Rechts (mm)",
  printMarginBottom: "Unten (mm)",
  printMarginLeft: "Links (mm)",
  printGapMm: "Abstand (mm)",
  printCustomizeSpacing: "Abstände anpassen",
  printLegibilityBreach: "QR-Code sehr klein (<6 mm) — auf manchen Laserdrucken möglicherweise schwer zu scannen",
  createByScan: "Neuer Behälter vom gescannten Etikett {id}",
  createConflict: "Dieses Etikett existiert bereits — stattdessen öffnen?",
  openInstead: "Vorhandenen Behälter öffnen",
  scanOpenContainer: "„{name}“ öffnen",
  openPhoto: "Foto öffnen",
  lightboxZoom: "Zoomen",
  lightboxZoomIn: "Vergrößern",
  lightboxZoomOut: "Verkleinern",
  lightboxPrev: "Vorheriges Foto",
  lightboxNext: "Nächstes Foto",
  mediaReplayHint: "Sprachnotizen lassen sich über die eingebauten Steuerelemente abspielen.",
  preview: "Vorschau",
  gallery: "Fotos",
  galleryEmpty: "Noch keine Fotos — auf der Detailseite eines Behälters hinzufügen.",
  loadError: "Laden fehlgeschlagen — ist der Server erreichbar?",
  copyUuid: "UUID kopieren",
  uuidCopied: "UUID in die Zwischenablage kopiert.",
  labelPreview: "Etikett",
  showLabelPreview: "Etikett anzeigen",
  // AI summary (Phase 2 / ticket 06)
  aiSummaryTitle: "KI-Zusammenfassung",
  aiSummaryByAi: "von KI",
  aiSummaryBlock: "KI-Zusammenfassungsblock",
  aiSummaryFieldDescription: "KI-Zusammenfassung für Beschreibung",
  aiSummaryFieldOwner: "KI-Zusammenfassung für Besitzer",
  aiSummaryGeneratedAt: "Generiert {date}",
  aiSummaryModel: "Modell: {model}",
  aiSummarySources: "Quellen",
  aiSummarySourceDescription: "Beschreibung",
  aiSummarySourceTranscript: "Transkript",
  aiSummarySourceDetection: "Erkennung",
  aiSummaryTranscript: "Transkript",
  aiSummaryMergedData: "Zusammengeführte Daten",
  aiSummaryDataOwner: "Besitzer: {owner}",
  aiSummaryReExtract: "Neu extrahieren",
  aiSummaryReExtractLabel: "KI-Zusammenfassung neu extrahieren",
  aiSummaryReExtracting: "Extrahiere neu…",
  aiSummaryReExtractQueued: "KI-Neu-Extraktion gestartet; die neue Version erscheint, sobald die Zusammenführung fertig ist.",
  aiSummaryReExtractFailed: "KI-Neu-Extraktion fehlgeschlagen",
  // Re-trigger (ticket 09)
  reTranscribe: "Neu transkribieren",
  reTranscribed: "Neu transkribiert",
  reTranscribeFailed: "Neu-Transkription fehlgeschlagen",
  showTranscript: "Transkript anzeigen",
  hideTranscript: "Transkript ausblenden",
  rePreview: "Vorschau neu erzeugen",
  rePreviewed: "Vorschau neu erzeugt",
  rePreviewFailed: "Vorschau-Neuerzeugung fehlgeschlagen",
  // Manual transcript override (ADR-0035, manual-stt-edit 03)
  manualTranscript: "manuell",
  editTranscript: "Transkript bearbeiten",
  editTranscriptTitle: "Transkript bearbeiten",
  transcriptField: "Transkript",
  transcriptEmptyHint: "Leer lassen, um diese Sprachnotiz als ohne Transkript zu markieren.",
  originalTranscript: "Originaltranskript",
  showOriginalTranscript: "Original anzeigen",
  hideOriginalTranscript: "Original ausblenden",
  copyTranscript: "Original kopieren",
  transcriptCopied: "Transkript kopiert.",
  transcriptCopyFailed: "Transkript konnte nicht kopiert werden.",
  transcriptSaved: "Transkript gespeichert.",
  transcriptSaveFailed: "Transkript konnte nicht gespeichert werden.",
  resetTranscript: "Auf automatisches Transkript zurücksetzen",
  transcriptReset: "Auf das automatische Transkript zurückgesetzt.",
  transcriptResetFailed: "Transkript konnte nicht zurückgesetzt werden.",
  items: "Artikel",
  itemsEmpty: "Noch keine Artikel — scanne einen Barcode, um einen hinzuzufügen.",
  scanBarcode: "Barcode scannen",
  incrementQuantity: "Menge erhöhen",
  decrementQuantity: "Menge verringern",
  removeItem: "Artikel entfernen",
  scanToAddTitle: "Scannen zum Hinzufügen",
  scanToAddBarcodePlaceholder: "Oder Barcode manuell eingeben",
  scanToAddLookup: "Nachschlagen",
  scanToAddConfirm: "Zum Behälter hinzufügen",
  scanToAddUnknown: "Barcode {barcode} nicht gefunden — Details unten eingeben.",
  scanToAddNameLabel: "Artikelname",
  scanToAddLookupFailed: "Nachschlagen fehlgeschlagen — bitte erneut versuchen.",
  scanToAddFailed: "Artikel konnte nicht hinzugefügt werden.",
  scanToAddAlreadyHere: "Bereits in diesem Behälter: {count}",
  scanToAddNewTotal: "Neue Gesamtzahl in diesem Behälter",
  scanToAddAdded: "{name} hinzugefügt",
  scanToAddDone: "Fertig",
  createItemTitle: "Artikel hinzufügen",
  createItemConfirm: "Artikel anlegen",
  createItemFromLabelTitle: "Neuer Artikel aus Etikett {id}",
  // Global scan quantity block (results-03: immediate write, one-level undo)
  scanAdjustChoosePlacement: "Ablageort wählen",
  scanAdjustContainer: "Behälter",
  scanAdjustImmediate: "Wird sofort gespeichert",
  scanAdjustSaved: "Anzahl aktualisiert: {name}",
  scanAdjustFailed: "Anzahl konnte nicht aktualisiert werden.",
  scanAdjustFailedReason: "Anzahl konnte nicht aktualisiert werden: {reason}",
  offlineNotSaved: "Offline — nicht gespeichert.",
  category_book: "Buch",
  category_food: "Lebensmittel",
  category_device: "Gerät",
  category_media: "Medien",
  category_other: "Sonstiges",
  itemsList: "Artikel",
  itemListEmpty: "Noch keine Artikel — scanne einen Barcode, um einen hinzuzufügen.",
  itemDetail: "Artikeldetail",
  itemPhotos: "Fotos",
  itemAddPhoto: "Foto hinzufügen",
  itemAddPhotoFailed: "Foto konnte nicht hinzugefügt werden.",
  itemRemovePhoto: "Foto entfernen",
  itemPhotoMoveLeft: "Foto nach links verschieben",
  itemPhotoMoveRight: "Foto nach rechts verschieben",
  itemPhotoSetCover: "Als Titelbild festlegen",
  itemPhotosReordered: "Fotoreihenfolge gespeichert",
  itemReorderPhotoFailed: "Fotos konnten nicht umsortiert werden.",
  itemNutrition: "Nährwerte",
  itemContainers: "Behälter",
  itemRefresh: "Aktualisieren",
  itemDelete: "Artikel löschen",
  itemDeleteError: "Artikel konnte nicht gelöscht werden: {error}",
  itemDeleteConfirmTitle: "Diesen Artikel löschen?",
  itemDeleteConfirmBody: "Dieser Artikel wird dauerhaft gelöscht.",
  itemMetadata: "Metadaten",
  itemEdit: "Bearbeiten",
  itemEditDialogTitle: "Artikel bearbeiten",
  itemEditName: "Name",
  itemEditDescription: "Beschreibung",
  itemEditKey: "Schlüssel",
  itemEditValue: "Wert",
  itemEditAddRow: "Zeile hinzufügen",
  itemEditRemoveRow: "Zeile entfernen",
  itemEditSaved: "Artikel gespeichert",
  itemEditSaveError: "Artikel konnte nicht gespeichert werden.",
  itemNoContainers: "In keinem Behälter",
  itemNoPhotos: "Noch keine Fotos — füge eins mit der Kamera oder aus Dateien hinzu.",
  sortAlpha: "A–Z",
  stagedPhotoHint: "Fotos mit der Kamera oder aus Dateien hinzufügen — sie werden beim Speichern angehängt.",
  stagedPhotoCount: "{count} Fotos für diesen Artikel bereit",
  stagedPhotoCountOne: "{count} Foto für diesen Artikel bereit",
  sortNewest: "Neueste",
  sortCategory: "Typ",
  sortSoonest: "Baldigste",
  // Deadlines (deadlines 02, ADR-0037)
  deadlines: "Fristen",
  deadlineColumn: "Frist",
  deadlineMore: "+{count} weitere",
  deadlineNoneDue: "Keine Fristen fällig",
  deadlineOverdue: "Überfällig",
  deadlineSoon: "Bald fällig",
  deadlineOverdueCount: "{count} überfällig",
  deadlineSoonCount: "{count} bald",
  deadlineFilters: "Fristfilter",
  deadlineLabel: "Frist",
  deadlineFilterEmpty: "Nichts entspricht diesen Fristfiltern.",
  deadlineSortBySoonest: "Nach nächster Frist sortieren",
  deadlineSortByName: "Nach nächster Frist sortiert; zurück zur Namenssortierung",
  deadlineToday: "heute",
  deadlineYesterday: "gestern",
  deadlineInvalidDate: "Gültiges Datum eingeben (JJJJ-MM-TT).",
  deadlineBestBefore: "Mindesthaltbar bis",
  deadlineReturnBy: "Zurück bis",
  deadlineExpiresOn: "Läuft ab am",
  deadlineWarrantyUntil: "Garantie bis",
  deadlinePurchasedOn: "Gekauft am",
  clearDate: "Datum löschen",
  itemDates: "Daten",
  bulkEdit: "Auswählen",
  bulkRefresh: "Aktualisieren ({count})",
  bulkDelete: "Löschen ({count})",
  selectItems: "Artikel auswählen",
  cancelSelection: "Abbrechen",
  cancelSelectionMode: "Auswahl abbrechen",
  open: "Öffnen",
  upOneLevel: "Eine Ebene nach oben",
  destinationPath: "Zielpfad",
  drillInto: "{name} öffnen",
  refreshProgress: "Wird aktualisiert…",
  bulkDeleteConfirmTitle: "Ausgewählte Artikel löschen?",
  bulkDeleteConfirmBody: "{count} Artikel werden dauerhaft gelöscht.",
  bulkDeleteConfirmBodyOne: "{count} Artikel wird dauerhaft gelöscht.",
  searchPlaceholderItems: "Artikel suchen…",
  themeLight: "Hell",
  themeDark: "Dunkel",
  themeSystem: "System",
  profile: "Profil",
  // Catalog entries + sharing (ticket 07)
  catalog: "Katalog",
  catalogs: "Kataloge",
  currentCatalog: "Aktueller Katalog",
  manage: "Verwalten",
  defaultCatalogHint: "Neue oberste Behälter landen im Standardkatalog.",
  myCatalogs: "Meine Kataloge",
  sharedWithMe: "Mit mir geteilt",
  sharedFrom: "von {owner}",
  sharedBy: "Geteilt von {owner}",
  newEntry: "Neuer Katalog",
  entryName: "Katalogname",
  createEntryTitle: "Katalog anlegen",
  create: "Erstellen",
  entryEmpty: "Noch keine Kataloge – lege deinen ersten an.",
  defaultEntry: "Standard",
  entryRename: "Umbenennen",
  entrySetDefault: "Als Standard",
  entryDelete: "Katalog löschen",
  entryShare: "Teilen…",
  deleteEntryConfirmTitle: "Katalog „{name}“ löschen?",
  deleteEntryConfirmBody: "Gib zur Bestätigung den Katalognamen ein. Alle von diesem Katalog erreichbaren Behälter wandern in den Papierkorb.",
  shareTitle: "Katalog freigeben",
  sharees: "Freigegeben für",
  shareesEmpty: "Noch niemand – teile den Katalog mit einem anderen Benutzer.",
  revoke: "Entziehen",
  levelOwner: "Besitzer",
  levelAdmin: "Admin",
  levelWrite: "Schreiben",
  levelRead: "Lesen",
  catalogAssignment: "Katalog",
  formEntry: "Katalogeintrag",
  createEntryFallback: "Kein Schreibzugriff – verwende {entry}",
  sidebarItems: "Artikel",
  galleryFilterAll: "Alle",
  gallerySource: "Quelle",
  gallerySourceContainers: "Behälterfotos",
  gallerySourceItems: "Artikelfotos",
  galleryFilterGeneral: "Allgemein",
  galleryFilterNutrition: "Nährwerte",
  goToContainer: "Zum Behälter",
  goToItem: "Zum Artikel",
  // Gallery image search (ticket 09)
  galleryMode: "Galeriemodus",
  galleryModeBrowse: "Alle Fotos",
  galleryModeAppearance: "Nach Aussehen",
  appearancePlaceholder: "Beschreibe ein Foto…",
  appearanceLoading: "Wird gesucht…",
  appearanceDownloading: "Suchmodell wird heruntergeladen…",
  appearanceOffline: "Die Bildsuche muss ihr Modell einmal online herunterladen.",
  appearanceError: "Bildsuche fehlgeschlagen – ist der Server erreichbar?",
  appearanceNoMatches: "Keine passenden Fotos – versuche eine andere Beschreibung.",
  // Search result disclosure (search-result-disclosure 03–05)
  filterOpen: "Suchfilter öffnen",
  filterClose: "Suchfilter ausblenden",
  filterScope: "Bereich",
  filterOwner: "Besitzer",
  filterTag: "Tag",
  filterType: "Typ",
  filterDisplay: "Anzeige",
  filterShow: "zeigen",
  filterHide: "ausblenden",
  filterClearAll: "Alle löschen",
  filterSemantics:
    "ODER innerhalb einer Zeile · UND über Zeilen · Werte und Anzahl folgen den anderen Filtern",
  filterNoneLeft: "keine übrig",
  filterAny: "Beliebig",
  filterPreviews: "Vorschauen",
  filterDescriptions: "Beschreibungen",
  filterUnassigned: "Nicht zugewiesen",
  searchClearFilters: "Filter zurücksetzen",
  searchUnplaced: "in keinem Behälter",
  searchOpen: "Öffnen",
  searchContents: "Inhalt",
  searchPhotoCount: "{count} Fotos",
  searchPhotoCountOne: "{count} Foto",
  searchLess: "Weniger",
  searchPlacements: "Platzierungen",
  searchInContainers: "In {count} Behältern",
  openContainer: "Behälter öffnen",
  undo: "Rückgängig",
  placementRemoved: "{name} aus diesem Behälter entfernt.",
  searchMoreItems: "+{count} weitere",
  searchNoPhotos: "Keine Fotos",
  relatedPhotos: "Weitere Fotos",
} satisfies Record<MessageKey, string>;
